import React, { useEffect, useState } from "react";
import { collection, onSnapshot, addDoc, updateDoc, doc, arrayUnion, serverTimestamp, query, orderBy, deleteDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../firebase";

export default function ObdUsedPanel({ brand, model }) {
  const [obdList, setObdList] = useState([]);
  const [newObdName, setNewObdName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // { obdId, fileName }

  useEffect(() => {
    if (!brand || !model) return;

    const q = query(collection(db, "EIS", brand, "Models", model, "OBDused"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snap) => {
      setObdList(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    return () => unsubscribe();
  }, [brand, model]);

  const handleAddObd = async (e) => {
    e.preventDefault();
    if (!newObdName.trim()) return;

    try {
      await addDoc(collection(db, "EIS", brand, "Models", model, "OBDused"), {
        name: newObdName.trim(),
        createdAt: serverTimestamp(),
        files: []
      });
      setNewObdName("");
    } catch (err) {
      console.error("Error adding OBD:", err);
      alert("Failed to add OBD");
    }
  };

  const handleDeleteObd = async (obdId) => {
    if (!window.confirm("Are you sure you want to delete this OBD entry?")) return;
    try {
      await deleteDoc(doc(db, "EIS", brand, "Models", model, "OBDused", obdId));
    } catch (err) {
      console.error("Error deleting OBD:", err);
      alert("Failed to delete OBD");
    }
  }

  const handleFileUpload = async (e, obdId) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadProgress({ obdId, fileName: file.name });

    try {
      // Path: EIS/{brand}/{model}/OBDused/{obdId}/{filename}
      const storageRef = ref(storage, `EIS/${brand}/${model}/OBDused/${obdId}/${file.name}`);

      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      // Update Firestore
      await updateDoc(doc(db, "EIS", brand, "Models", model, "OBDused", obdId), {
        files: arrayUnion({
          name: file.name,
          url: url,
          type: file.type,
          uploadedAt: new Date().toISOString()
        })
      });

    } catch (err) {
      console.error("Upload failed", err);
      alert("File upload failed: " + err.message);
    } finally {
      setUploading(false);
      setUploadProgress(null);
      e.target.value = null; // Reset input
    }
  };

  return (
    <div className="space-y-6">

      {/* Add New OBD Section */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-100">
        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Add OBD Device</h3>
        <form onSubmit={handleAddObd} className="flex gap-2">
          <input
            type="text"
            value={newObdName}
            onChange={(e) => setNewObdName(e.target.value)}
            placeholder="Enter OBD Name (e.g. OBD-A, OBD-B)..."
            className="flex-1 bg-slate-50 border border-slate-200 rounded-lg py-2 px-3 text-sm font-bold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all placeholder:font-medium placeholder:text-slate-400"
          />
          <button
            type="submit"
            disabled={!newObdName.trim()}
            className="bg-slate-900 text-white px-4 py-2 rounded-lg text-xs font-bold uppercase hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            Add OBD
          </button>
        </form>
      </div>

      {/* OBD List */}
      <div className="grid grid-cols-1 gap-4">
        {obdList.map(obd => (
          <div key={obd.id} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            {/* Header */}
            <div className="bg-slate-50 px-4 py-3 border-b border-slate-100 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-xs">
                  OBD
                </div>
                <h4 className="text-sm font-black text-slate-800">{obd.name}</h4>
              </div>
              <button
                onClick={() => handleDeleteObd(obd.id)}
                className="text-slate-400 hover:text-red-500 transition-colors p-1"
                title="Delete OBD"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
              </button>
            </div>

            {/* Files Section */}
            <div className="p-4">
              <div className="space-y-2 mb-4">
                {obd.files && obd.files.map((file, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 rounded-lg bg-slate-50 border border-slate-100 group hover:border-indigo-200 transition-all">
                    <div className="flex items-center gap-3">
                      {/* File Icon */}
                      <div className={`w-8 h-8 rounded flex items-center justify-center shrink-0 ${file.name.endsWith('.pdf') ? 'bg-red-50 text-red-500' :
                        file.name.endsWith('.zip') ? 'bg-yellow-50 text-yellow-600' :
                          'bg-blue-50 text-blue-500'
                        }`}>
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          {file.name.endsWith('.zip')
                            ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                            : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          }
                        </svg>
                      </div>

                      <div className="flex flex-col overflow-hidden">
                        <a
                          href={file.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs font-bold text-slate-700 hover:text-indigo-600 truncate underline decoration-dotted decoration-slate-300 hover:decoration-indigo-500"
                        >
                          {file.name}
                        </a>
                        <span className="text-[10px] text-slate-400 font-mono">
                          {new Date(file.uploadedAt).toLocaleString()}
                        </span>
                      </div>
                    </div>

                    <a
                      href={file.url}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                      title="Download File"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                    </a>
                  </div>
                ))}

                {(!obd.files || obd.files.length === 0) && (
                  <div className="text-center py-4 text-xs text-slate-400 italic bg-slate-50/50 rounded-lg border border-dashed border-slate-200">
                    No files uploaded yet.
                  </div>
                )}
              </div>

              {/* Upload Button */}
              <div className="relative">
                <input
                  type="file"
                  id={`file-${obd.id}`}
                  className="hidden"
                  onChange={(e) => handleFileUpload(e, obd.id)}
                  disabled={uploading}
                />
                <label
                  htmlFor={`file-${obd.id}`}
                  className={`
                        w-full flex items-center justify-center gap-2 py-2 rounded-lg border border-dashed border-slate-300 text-slate-500 text-xs font-bold cursor-pointer hover:bg-slate-50 hover:border-slate-400 transition-all
                        ${uploading ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                >
                  {uploading && uploadProgress?.obdId === obd.id ? (
                    <>
                      <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin"></div>
                      <span>Uploading {uploadProgress.fileName}...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                      <span>Upload Code/File (PDF, ZIP)</span>
                    </>
                  )}
                </label>
              </div>
            </div>
          </div>
        ))}

        {obdList.length === 0 && (
          <div className="text-center py-10">
            <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3 text-slate-300">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" /></svg>
            </div>
            <h3 className="text-sm font-bold text-slate-400">No OBD Devices Added</h3>
            <p className="text-xs text-slate-300 mt-1">Add a device above to start uploading files.</p>
          </div>
        )}
      </div>
    </div>
  );
}
